import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Eye, EyeOff, Loader2, ArrowRight, Check, Sparkles, X } from 'lucide-react';
import { Field, inputCls, COLOR } from './atoms';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const Step1Basics = ({ form, errors, upd, setField, loading, onSubmit, showPassword, setShowPassword }) => {
  // iter 98 - Promo code field is collapsed by default. When the user types
  // a code we hit `/api/promo/validate` (debounced ~400ms, unauthenticated)
  // and show an inline success badge so they get instant confidence the
  // code will apply.
  const [promoOpen, setPromoOpen] = useState(!!form.promo_code);
  const [promoStatus, setPromoStatus] = useState(null); // null | 'checking' | 'valid' | 'invalid'
  const [promoMeta, setPromoMeta] = useState(null);

  useEffect(() => {
    const code = (form.promo_code || '').trim();
    if (!code) {
      setPromoStatus(null);
      setPromoMeta(null);
      return undefined;
    }
    setPromoStatus('checking');
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`${API_URL}/api/promo/validate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code }),
        });
        const d = await r.json();
        if (d.valid) {
          setPromoStatus('valid');
          setPromoMeta(d);
        } else {
          setPromoStatus('invalid');
          setPromoMeta(null);
        }
      } catch {
        setPromoStatus('invalid');
        setPromoMeta(null);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [form.promo_code]);

  return (
  <div data-testid="step-1">
    <div className="text-center mb-5">
      <h1 className="text-xl sm:text-2xl font-light" data-testid="start-headline">
        Two minutes from now, you're in.
      </h1>
      <p className="text-sm mt-1 text-[#5A6B73]" data-testid="start-subheading">
        Start free for 15 days. Cancel anytime.
      </p>
    </div>

    <form onSubmit={onSubmit} className="space-y-4" data-testid="start-step1-form" noValidate>
      <div className="grid grid-cols-2 gap-3">
        <Field label="First name" error={errors.first_name} testId="field-first-name">
          <input type="text" autoComplete="given-name" value={form.first_name}
            onChange={upd('first_name')} placeholder="Jamie"
            className={inputCls(errors.first_name)} data-testid="start-first-name" />
        </Field>
        <Field label="Last name" error={errors.last_name} testId="field-last-name">
          <input type="text" autoComplete="family-name" value={form.last_name}
            onChange={upd('last_name')} placeholder="Rivera"
            className={inputCls(errors.last_name)} data-testid="start-last-name" />
        </Field>
      </div>

      <Field label="Email" error={errors.email} testId="field-email">
        <input type="email" autoComplete="email" value={form.email}
          onChange={upd('email')} placeholder="you@example.com"
          className={inputCls(errors.email)} data-testid="start-email" />
      </Field>

      <Field label="Password" error={errors.password} testId="field-password">
        <div className="relative">
          <input type={showPassword ? 'text' : 'password'} autoComplete="new-password"
            value={form.password} onChange={upd('password')} minLength={8}
            placeholder="Min. 8 characters"
            className={inputCls(errors.password) + ' pr-12'} data-testid="start-password" />
          <button type="button" onClick={() => setShowPassword((v) => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 min-w-[44px] min-h-[44px] flex items-center justify-center text-[#5A6B73]"
            aria-label="Toggle password visibility" data-testid="start-password-toggle">
            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>
      </Field>

      {/* Iter 71 - required consent checkboxes (Terms + email opt-in). */}
      <div className="space-y-3 pt-2" data-testid="signup-consent-block">
        <label className="flex items-start gap-3 text-[13px] text-[#0A1F2E]/85 leading-snug cursor-pointer">
          <input
            type="checkbox"
            checked={!!form.terms_accepted}
            onChange={(e) => setField('terms_accepted', e.target.checked)}
            className="mt-0.5 w-4 h-4 rounded border-[#0A1F2E]/30 text-[#2CA8A0] focus:ring-[#2CA8A0]/40 flex-shrink-0"
            data-testid="signup-terms-checkbox"
          />
          <span>
            I have read and agree to the{' '}
            <Link
              to="/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#1A5C6E] underline hover:text-[#0A1F2E]"
              data-testid="signup-terms-link"
            >
              Terms of Service
            </Link>.
          </span>
        </label>
        {errors.terms_accepted && (
          <p className="text-xs text-[#B91C1C] -mt-1 ml-7" data-testid="signup-terms-error">
            {errors.terms_accepted}
          </p>
        )}

        <label className="flex items-start gap-3 text-[13px] text-[#0A1F2E]/85 leading-snug cursor-pointer">
          <input
            type="checkbox"
            checked={!!form.email_consent}
            onChange={(e) => setField('email_consent', e.target.checked)}
            className="mt-0.5 w-4 h-4 rounded border-[#0A1F2E]/30 text-[#2CA8A0] focus:ring-[#2CA8A0]/40 flex-shrink-0"
            data-testid="signup-email-consent-checkbox"
          />
          <span>
            I agree to receive emails from Mundalo including my Blueprint,
            coaching confirmations, and community updates. I can unsubscribe at
            any time.
          </span>
        </label>
        {errors.email_consent && (
          <p className="text-xs text-[#B91C1C] -mt-1 ml-7" data-testid="signup-email-consent-error">
            {errors.email_consent}
          </p>
        )}
      </div>

      {/* iter 98 - collapsible promo code field. */}
      <div className="pt-2" data-testid="promo-block">
        {!promoOpen ? (
          <button
            type="button"
            onClick={() => setPromoOpen(true)}
            className="text-[13px] text-[#1A5C6E] hover:text-[#0A1F2E] underline"
            data-testid="promo-toggle"
          >
            Have a promo code?
          </button>
        ) : (
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label htmlFor="promo-code" className="text-[13px] font-medium text-[#0A1F2E]">
                Promo code
              </label>
              <button
                type="button"
                onClick={() => { setPromoOpen(false); setField('promo_code', ''); }}
                className="text-[11px] text-[#5A6B73] hover:text-[#0A1F2E] inline-flex items-center gap-1"
                data-testid="promo-remove"
              >
                <X className="w-3 h-3" /> remove
              </button>
            </div>
            <div className="relative">
              <input
                id="promo-code"
                type="text"
                value={form.promo_code}
                onChange={(e) => setField('promo_code', e.target.value.toUpperCase())}
                placeholder="e.g. WELCOME30"
                autoComplete="off"
                className={inputCls(false) + ' pr-10 font-mono tracking-wider uppercase'}
                data-testid="start-promo-code"
              />
              {promoStatus === 'checking' && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-[#5A6B73]" />
              )}
              {promoStatus === 'valid' && (
                <Check className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#2CA8A0]" />
              )}
              {promoStatus === 'invalid' && (
                <X className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#C97A4A]" />
              )}
            </div>
            {promoStatus === 'valid' && promoMeta && (
              <div
                className="mt-2 flex items-start gap-2 bg-[#2CA8A0]/10 border border-[#2CA8A0]/40 rounded-lg px-3 py-2 text-[12px] text-[#0A1F2E]"
                data-testid="promo-success-badge"
              >
                <Sparkles className="w-3.5 h-3.5 text-[#2CA8A0] mt-0.5 shrink-0" />
                <span>
                  <strong>Code valid.</strong> Unlocks <span className="capitalize">{promoMeta.grants_role}</span> tier for {promoMeta.duration_days} days, free.
                </span>
              </div>
            )}
            {promoStatus === 'invalid' && (
              <p className="mt-2 text-[12px] text-[#C97A4A]" data-testid="promo-invalid">
                That code isn't valid. Double-check the spelling.
              </p>
            )}
          </div>
        )}
      </div>

      <button type="submit" disabled={loading}
        className="w-full inline-flex items-center justify-center gap-2 py-3.5 mt-2 font-mono uppercase text-[12px] tracking-[0.15em] font-semibold rounded-full transition disabled:opacity-60"
        style={{ backgroundColor: COLOR.gold, color: COLOR.navy }}
        data-testid="start-submit">
        {loading && <Loader2 className="w-4 h-4 animate-spin" />}
        {loading ? 'Redirecting to secure checkout…' : 'Continue to secure checkout'}
        {!loading && <ArrowRight className="w-4 h-4" />}
      </button>

      {/* iter 182.g - subscription disclosure required before hand-off to
          Stripe Checkout. Three bullets on what membership includes,
          then the plain-language billing terms with the exact first-charge
          date. No dark patterns. */}
      <div
        className="mt-4 rounded-xl border border-[#E0D6B8] bg-[#F9F6EF] p-4 text-[12px] text-[#0A1F2E]"
        data-testid="trial-terms-disclosure"
      >
        <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-[#5A6B73] mb-2">
          Your Mundalo membership includes
        </p>
        <ul className="space-y-1.5 mb-3">
          <li className="flex gap-2">
            <Check className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#2CA8A0]" />
            <span><strong>Community.</strong> Events, boards, peer circles.</span>
          </li>
          <li className="flex gap-2">
            <Check className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#2CA8A0]" />
            <span><strong>Full Classroom access.</strong> Every course and workshop.</span>
          </li>
          <li className="flex gap-2">
            <Check className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#2CA8A0]" />
            <span><strong>Vetted Partner network.</strong> Attorneys, movers, brokers.</span>
          </li>
        </ul>
        <p className="text-[12px] leading-relaxed" data-testid="trial-terms-line">
          <strong>Free for 15 days, then $9.99/month.</strong> Cancel anytime.
          Card required at signup. First charge on{' '}
          <span data-testid="trial-terms-first-charge">
            {(() => {
              const d = new Date();
              d.setDate(d.getDate() + 15);
              return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'America/Chicago' });
            })()}
          </span>
          {' '}unless you cancel from your account portal.
        </p>
      </div>
    </form>

    <p className="text-center text-sm mt-5 text-[#5A6B73]">
      Already a member?{' '}
      <Link to="/portal" className="font-semibold text-[#0A1F2E] underline" data-testid="start-signin-link">
        Sign in
      </Link>
    </p>
  </div>
  );
};

export default Step1Basics;
